/*!
 * armastus-search.js
 * In-house Armastus search for the Shopify storefront.
 * Replaces the Impresee predictive dropdown + search-results grid + adds image search.
 *
 * Self-contained, no build step, no dependencies. Loaded with <script defer>.
 * Reads config from window.ArmastusSearch (injected by snippets/search-form.liquid).
 *
 * Degrades gracefully to native Shopify search whenever the API is unreachable:
 *   - dropdown errors -> hide our list, let the native <form> submit to /search
 *   - results-page errors -> reveal the native Liquid grid (SEO/no-JS fallback)
 *
 * API base (CORS *): https://api.armastus.app/api/v1/armastus/public
 */
(function () {
  'use strict';

  /* -------------------------------------------------------------------------
   * Config (with safe defaults so a missing window.ArmastusSearch never throws)
   * ---------------------------------------------------------------------- */
  var CFG = (window.ArmastusSearch && typeof window.ArmastusSearch === 'object')
    ? window.ArmastusSearch
    : {};

  var API_BASE = (CFG.apiBase || 'https://api.armastus.app/api/v1/armastus/public').replace(/\/+$/, '');
  var LANG = CFG.lang === 'ar' ? 'ar' : 'en';
  var RTL = !!CFG.rtl;
  var MONEY_FORMAT = CFG.moneyFormat || '₪{{amount}}'; // ₪ + amount fallback
  var SEARCH_URL = CFG.searchUrl || '/search';
  var SOURCE = 'website';
  var MAX_PAGE = 200;        // API caps found/totalPages at 200
  var DROPDOWN_LIMIT = 6;    // richer dropdown cards (price + image)
  var RESULTS_LIMIT = 24;    // results-page page size (API max 50)
  var MIN_QUERY = 3;         // call API only for length > 2
  var MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB

  // Image-search (WMS-parity) tunables
  var MAX_EDGE = 1024;            // downscale the query photo to this longest edge before upload
  var JPEG_QUALITY = 0.85;        // re-encode quality for cropped/downscaled uploads
  var AUTO_DETECT_MIN_SCORE = 0.5; // confidence below this -> ignore the auto-detected box
  var MIN_BOX = 0.08;            // smallest crop box (fraction) so handles never collapse
  var DEFAULT_BOX = { x: 0.15, y: 0.15, w: 0.7, h: 0.7 }; // centered 70% fallback box
  var VISUAL_RESULTS_LIMIT = 30;  // cap cards rendered in the overlay
  // Overlay grid: 2-up on phones, 3-up on tablet+ (viewport media queries can't see the
  // modal width, so we drop the lg-4 class to avoid cramming 4 columns into a narrow panel).
  var OVERLAY_GRID_CLS = 'grid rwcols-2 rwcols-md-3 grid-products';

  // i18n strings (Liquid provides translated values; these are EN fallbacks)
  var I18N = CFG.i18n || {};
  function t(key, fallbackEn, fallbackAr) {
    if (I18N[key]) return I18N[key];
    return LANG === 'ar' ? (fallbackAr || fallbackEn) : fallbackEn;
  }
  var STR = {
    noResults: function () { return t('noResults', 'No results found', 'لا توجد نتائج'); },
    seeAll: function () { return t('seeAll', 'See all results', 'عرض كل النتائج'); },
    loadMore: function () { return t('loadMore', 'Load more', 'تحميل المزيد'); },
    loading: function () { return t('loading', 'Loading', 'جار التحميل'); },
    sortRelevance: function () { return t('sortRelevance', 'Relevance', 'الصلة'); },
    sortNewest: function () { return t('sortNewest', 'Newest', 'الأحدث'); },
    inStockOnly: function () { return t('inStockOnly', 'In stock only', 'المتوفر فقط'); },
    soldOut: function () { return t('soldOut', 'Sold out', 'نفد المخزون'); },
    visualMatches: function () { return t('visualMatches', 'Visual matches', 'نتائج بالصورة'); },
    searchByImage: function () { return t('searchByImage', 'Search by image', 'البحث بالصورة'); },
    imageTooLarge: function () { return t('imageTooLarge', 'Image is too large (max 5MB).', 'الصورة كبيرة جداً (الحد الأقصى 5 ميجابايت).'); },
    imageWrongType: function () { return t('imageWrongType', 'Please choose an image file.', 'يرجى اختيار ملف صورة.'); },
    imageWarming: function () { return t('imageWarming', 'Image search is warming up. Please try again in a moment.', 'البحث بالصورة قيد التهيئة. حاول مرة أخرى بعد قليل.'); },
    imageBusy: function () { return t('imageBusy', 'Too many requests. Please wait a moment and try again.', 'طلبات كثيرة. يرجى الانتظار قليلاً ثم المحاولة.'); },
    imageError: function () { return t('imageError', 'Could not search by image. Please try again.', 'تعذّر البحث بالصورة. حاول مرة أخرى.'); },
    imageUnreadable: function () { return t('imageUnreadable', 'That image could not be read. Try another photo.', 'تعذّرت قراءة الصورة. جرّب صورة أخرى.'); },
    retry: function () { return t('retry', 'Retry', 'إعادة المحاولة'); },
    // Image-search overlay
    searchByPhoto: function () { return t('searchByPhoto', 'Search by photo', 'البحث بالصورة'); },
    searchThisArea: function () { return t('searchThisArea', 'Search this area', 'ابحث في هذه المنطقة'); },
    searchWholePhoto: function () { return t('searchWholePhoto', 'Search whole photo', 'ابحث في الصورة كاملة'); },
    resetArea: function () { return t('resetArea', 'Reset area', 'إعادة ضبط المنطقة'); },
    choosePhoto: function () { return t('choosePhoto', 'Choose photo', 'اختر صورة'); },
    dragHint: function () { return t('dragHint', 'Drag the box to focus on one item.', 'حرّك المربع للتركيز على عنصر واحد.'); },
    findingItem: function () { return t('findingItem', 'Finding the item…', 'جارٍ تحديد العنصر…'); },
    searchingCatalog: function () { return t('searchingCatalog', 'Searching…', 'جارٍ البحث…'); },
    matches: function () { return t('matches', 'Matches', 'النتائج'); },
    close: function () { return t('close', 'Close', 'إغلاق'); },
    // Short labels used on phones so all four buttons fit one row
    searchThisAreaShort: function () { return t('searchThisAreaShort', 'This area', 'هذه المنطقة'); },
    searchWholePhotoShort: function () { return t('searchWholePhotoShort', 'Whole photo', 'الصورة كاملة'); },
    resetAreaShort: function () { return t('resetAreaShort', 'Reset', 'إعادة'); },
    choosePhotoShort: function () { return t('choosePhotoShort', 'New photo', 'صورة جديدة'); }
  };

  /* -------------------------------------------------------------------------
   * Tiny helpers
   * ---------------------------------------------------------------------- */
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var ctx = this, args = arguments;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function setBusy(el, busy) {
    if (!el) return;
    if (busy) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  }

  // Replicate Shopify's money_format token rendering from a decimal string.
  // Supports {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
  // {{amount_no_decimals_with_comma_separator}}, {{amount_with_apostrophe_separator}}.
  function formatMoney(decimalString) {
    var fmt = MONEY_FORMAT;
    if (decimalString == null || decimalString === '') return '';
    var num = parseFloat(String(decimalString).replace(/,/g, ''));
    if (!isFinite(num)) return '';

    function withDelims(value, precision, thousands, decimalSep) {
      var n = (Math.round(value * 100) / 100).toFixed(precision);
      var parts = n.split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var decPart = parts[1] ? decimalSep + parts[1] : '';
      return intPart + decPart;
    }

    function replaceToken(token, value) {
      // global, escaped placeholder match e.g. {{amount}}
      var re = new RegExp('\\{\\{\\s*' + token + '\\s*\\}\\}', 'g');
      fmt = fmt.replace(re, value);
    }

    replaceToken('amount_no_decimals_with_comma_separator', withDelims(num, 0, '.', ','));
    replaceToken('amount_with_comma_separator', withDelims(num, 2, '.', ','));
    replaceToken('amount_with_apostrophe_separator', withDelims(num, 2, "'", '.'));
    replaceToken('amount_no_decimals', withDelims(num, 0, ',', '.'));
    replaceToken('amount', withDelims(num, 2, ',', '.'));

    return fmt;
  }

  function isCompareGreater(price, compareAt) {
    var p = parseFloat(String(price || '').replace(/,/g, ''));
    var c = parseFloat(String(compareAt || '').replace(/,/g, ''));
    return isFinite(p) && isFinite(c) && c > p;
  }

  function productUrl(handle) {
    return '/products/' + encodeURIComponent(handle);
  }

  function clamp(n, lo, hi) { return Math.min(hi, Math.max(lo, n)); }

  /* -------------------------------------------------------------------------
   * Network
   * ---------------------------------------------------------------------- */
  function buildSearchUrl(params) {
    var u = API_BASE + '/search?';
    var pairs = [];
    Object.keys(params).forEach(function (k) {
      if (params[k] === undefined || params[k] === null || params[k] === '') return;
      pairs.push(encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
    });
    return u + pairs.join('&');
  }

  // GET /search -> JSON. Rejects on non-2xx or network error.
  function apiSearch(params) {
    return fetch(buildSearchUrl(params), { method: 'GET', credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('search ' + res.status);
        return res.json();
      });
  }

  // POST /search/click -> 204. Fire-and-forget, no CORS preflight.
  // Sent as application/x-www-form-urlencoded: it is a CORS "simple" content-type
  // (so no preflight) AND the API parses it via express.urlencoded() into req.body.
  // (text/plain would also skip the preflight, but the API would NOT parse it, so
  // the click would be dropped.) Uses sendBeacon so it survives the navigation a
  // result tap triggers; falls back to a keepalive fetch.
  function postClick(q, handle) {
    try {
      var url = API_BASE + '/search/click';
      var params = new URLSearchParams();
      params.set('q', q || '');
      params.set('handle', handle || '');
      params.set('source', SOURCE);
      if (navigator && typeof navigator.sendBeacon === 'function') {
        // sendBeacon with URLSearchParams sends form-urlencoded automatically.
        if (navigator.sendBeacon(url, params)) return;
      }
      fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: params.toString(),
        credentials: 'omit'
      }).catch(function () {});
    } catch (e) { /* never block navigation */ }
  }

  // POST /visual-search (multipart). Accepts a File or a cropped Blob. Resolves JSON,
  // rejects Error with .status. A filename is always supplied so the server's image
  // filter accepts a canvas Blob (which has no name of its own).
  function apiVisualSearch(blob) {
    var fd = new FormData();
    fd.append('image', blob, (blob && blob.name) ? blob.name : 'query.jpg');
    // Bare fetch: browser sets multipart boundary; do NOT set Content-Type.
    return fetch(API_BASE + '/visual-search', { method: 'POST', body: fd, credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) {
          var err = new Error('visual-search ' + res.status);
          err.status = res.status;
          throw err;
        }
        return res.json();
      });
  }

  // POST /visual-search/detect (multipart). Best-effort garment detection — resolves
  // { detections:[{box,score,label}] }; returns { detections:[] } on ANY error/timeout
  // so the caller falls back to a centered box / whole-photo search.
  function apiVisualDetect(blob) {
    var fd = new FormData();
    fd.append('image', blob, (blob && blob.name) ? blob.name : 'query.jpg');
    var ctrl = ('AbortController' in window) ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, 20000) : null;
    return fetch(API_BASE + '/visual-search/detect', {
      method: 'POST', body: fd, credentials: 'omit', signal: ctrl ? ctrl.signal : undefined
    }).then(function (res) {
      if (timer) clearTimeout(timer);
      if (!res.ok) throw new Error('detect ' + res.status);
      return res.json();
    }).catch(function () {
      if (timer) clearTimeout(timer);
      return { detections: [] };
    });
  }

  /* -------------------------------------------------------------------------
   * Shared card renderer (mirrors the theme's native markup/classes)
   * ---------------------------------------------------------------------- */

  // Compact card for the dropdown (mirrors snippets/product-card-columns.liquid).
  function dropdownCardHTML(p) {
    var url = productUrl(p.handle);
    var title = escapeHtml(p.title || '');
    var img = p.image_url
      ? '<img src="' + escapeHtml(p.image_url) + '" alt="' + title + '" loading="lazy" class="img-fit" width="80" height="100">'
      : '';
    var price = formatMoney(p.price);
    var priceHtml = '';
    if (price) {
      // Dropdown shows only the current price (sale-coloured when discounted) — no
      // compare-at strikethrough, which crowded the compact row. The results-page
      // card (gridCardHTML) still shows the struck compare-at.
      var onSale = isCompareGreater(p.price, p.compare_at_price);
      priceHtml = '<div class="grid-price mb10"><span class="product-price' +
        (onSale ? ' sale' : '') + '">' + escapeHtml(price) + '</span></div>';
    }
    // SKU shows in the predictive DROPDOWN (per request), not on the results grid.
    var sku = p.sku
      ? '<div class="grid-sku mb5">' + escapeHtml(p.sku) + '</div>'
      : '';
    var soldOut = (p.in_stock === false)
      ? '<div class="armastus-soldout">' + escapeHtml(STR.soldOut()) + '</div>'
      : '';

    return '' +
      '<a class="armastus-card-link fl f-aic of-hidden gap mb15" data-armastus-handle="' + escapeHtml(p.handle || '') + '"' +
        ' href="' + escapeHtml(url) + '" style="--gap:15px;--gapm:15px;">' +
        '<div class="mini-products__img">' +
          '<span class="grid-image" aria-hidden="true">' + img + '</span>' +
        '</div>' +
        '<div class="fl1">' +
          '<div class="product-title mb5"><span>' + title + '</span></div>' +
          priceHtml +
          sku +
          soldOut +
        '</div>' +
      '</a>';
  }

  // Shopify CDN images accept a width param; append it for card/swatch sizes.
  function shopifyImg(url, width) {
    if (!url) return url;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + width;
  }

  // Reuse the theme's grid container classes (copied from the live #collection-products
  // element) so columns + spacing + padding match the native collection exactly.
  function nativeGridClasses() {
    var native = document.querySelector('#collection-products');
    if (native && native.className) return native.className;
    return 'grid rwcols-2 rwcols-md-3 rwcols-lg-4 grid-products';
  }

  // The native results live inside <collection-filters-form id="Collection"
  // class="facets … page-width|fw-section">, which supplies the page's left/right
  // gutter + max-width + centering. Our #armastus-results mount is a bare child of
  // .section-cover (no gutter), so the grid's negative margins pull the first column
  // to the screen edge. Copy the SAME width token(s) onto our wrapper so the grid
  // aligns exactly like a native collection.
  function nativeContainerClasses() {
    var c = document.querySelector('#Collection');
    if (c && c.className) {
      var toks = c.className.split(/\s+/).filter(function (x) {
        return x === 'page-width' || x === 'fw-section' || x === 'npd';
      });
      if (toks.length) return toks.join(' ');
    }
    return 'page-width';
  }

  // Color swatches in the theme's IMAGE-swatch (vrimage) format. Renders only colors
  // that have a variant image; caps at 6 (theme clcount) + a "N+" indicator.
  // `activeColor` (optional) pre-marks the matching swatch .active — used by image
  // search to highlight the colour that matched the photo (card already opens on it).
  function swatchListHTML(colors, activeColor) {
    if (!Array.isArray(colors) || !colors.length) return '';
    var shown = [];
    for (var i = 0; i < colors.length; i++) {
      if (colors[i] && colors[i].image_url) shown.push(colors[i]);
    }
    if (!shown.length) return '';
    var active = (activeColor || '').toLowerCase();
    var CAP = 6, lis = '';
    for (var j = 0; j < shown.length && j < CAP; j++) {
      var name = escapeHtml(shown[j].name || '');
      var thumb = escapeHtml(shopifyImg(shown[j].image_url, 66));
      var full = escapeHtml(shopifyImg(shown[j].image_url, 500));
      var isActive = active && (shown[j].name || '').toLowerCase() === active ? ' active' : '';
      lis += '<li class="swatch background-image color-swatch' + isActive + '" title="' + name + '"' +
             ' style="background-image:url(\'' + thumb + '\');background-position:50% 0;" data-src="' + full + '">' +
             '<span class="tooltip-label top">' + name + '</span></li>';
    }
    if (shown.length > CAP) lis += '<li class="numb clcount">' + (shown.length - CAP) + '+</li>';
    return '<ul class="grid-swatches fl f-wrap f-aic f-jcc">' + lis + '</ul>';
  }

  // Full grid card matching snippets/product-card-grid.liquid for THIS store
  // (grid-1 box, round-xs image, image swatches, no sale badge, soldout style2),
  // so a JS-rendered result card is indistinguishable from a native collection card.
  // Adds a .grid-sku line (theme cards have none) and color swatches from result.colors.
  function gridCardHTML(p) {
    var url = productUrl(p.handle);
    var title = escapeHtml(p.title || '');
    var img = p.image_url
      ? '<img class="img-fit primary" src="' + escapeHtml(shopifyImg(p.image_url, 600)) + '" alt="' + title + '" loading="lazy">'
      : '';

    var price = formatMoney(p.price);
    var priceInner = '';
    if (price && isCompareGreater(p.price, p.compare_at_price)) {
      priceInner = '<span class="product-price sale">' + escapeHtml(price) + '</span> ' +
                   '<s class="product-price">' + escapeHtml(formatMoney(p.compare_at_price)) + '</s>';
    } else if (price) {
      priceInner = '<span class="product-price">' + escapeHtml(price) + '</span>';
    }
    var priceHtml = priceInner ? '<div class="grid-price"><div class="price">' + priceInner + '</div></div>' : '';

    var soldOut = (p.in_stock === false);
    var soldOutCls = soldOut ? ' sold-out style2' : '';
    var soldOutLbl = soldOut
      ? '<span class="lbl sold-out st2 pa fl f-aic f-jcc"><span>' + escapeHtml(STR.soldOut()) + '</span></span>'
      : '';

    var swatches = swatchListHTML(p.colors, p.matched_color);

    return '' +
      '<div class="gitem fl">' +
        '<div class="db grid-content grid-1' + soldOutCls + '" data-armastus-handle="' + escapeHtml(p.handle || '') + '">' +
          '<div class="grid-image__wrapper mb15 round-xs">' +
            '<a class="grid-image armastus-card-link" href="' + escapeHtml(url) + '" aria-label="' + title + '">' + img + '</a>' +
            soldOutLbl +
          '</div>' +
          '<div class="grid-meta anim">' +
            '<div class="grid-title product-title"><a class="armastus-card-link" href="' + escapeHtml(url) + '">' + title + '</a></div>' +
            priceHtml +
            swatches +
          '</div>' +
        '</div>' +
      '</div>';
  }

  // Intercept card-link clicks -> fire CTR beacon (never block nav). Skip swatch clicks.
  function bindCardClicks(container, getQuery) {
    if (!container || container.__armastusClickBound) return;
    container.__armastusClickBound = true;
    container.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.grid-swatches')) return;
      var link = e.target.closest ? e.target.closest('a[href]') : null;
      if (!link || !container.contains(link)) return;
      var cardEl = e.target.closest('[data-armastus-handle]');
      var handle = cardEl ? (cardEl.getAttribute('data-armastus-handle') || '') : '';
      if (handle) postClick(typeof getQuery === 'function' ? getQuery() : '', handle);
      // do not preventDefault — navigation proceeds normally
    }, true);
  }

  // Image-swatch click -> swap the card's main image (matches the theme behavior).
  // Delegated once on document so it covers results + visual + any future cards.
  function initSwatchSwap() {
    if (document.__armastusSwatchBound) return;
    document.__armastusSwatchBound = true;
    document.addEventListener('click', function (e) {
      var sw = e.target.closest ? e.target.closest('.color-swatch') : null;
      if (!sw) return;
      var src = sw.getAttribute('data-src');
      if (!src) return;
      e.preventDefault();
      e.stopPropagation();
      var card = sw.closest('.grid-content');
      var img = card && card.querySelector('.img-fit.primary, .grid-image img');
      if (img) { img.removeAttribute('srcset'); img.src = src; }
      var sibs = sw.parentNode ? sw.parentNode.querySelectorAll('.color-swatch') : [];
      for (var i = 0; i < sibs.length; i++) sibs[i].classList.remove('active');
      sw.classList.add('active');
    }, true);
  }

  /* -------------------------------------------------------------------------
   * One-time scoped styles
   * ---------------------------------------------------------------------- */
  function injectStylesOnce() {
    if (document.getElementById('armastus-search-styles')) return;
    var css =
      '.armastus-card-link{text-decoration:none;color:inherit}' +
      // dropdown thumb: give the ratio-box a fallback so it never collapses where --grid-img_ratio is unset
      '.mini-products__img .grid-image{display:block;position:relative;padding-bottom:var(--grid-img_ratio,125%)}' +
      '#armastus-results{display:block}' +
      // SKU line — shown in the predictive dropdown card
      '.grid-sku{font-size:12px;opacity:.62;line-height:1.3;word-break:break-word}' +
      '.armastus-soldout{font-size:12px;opacity:.6;margin-top:4px}' +
      '.armastus-toolbar{display:flex;flex-wrap:wrap;align-items:center;gap:12px;margin-bottom:20px}' +
      '.armastus-toolbar label{display:inline-flex;align-items:center;gap:6px;font-size:13px;cursor:pointer}' +
      '.armastus-toolbar select{padding:6px 10px;border:1px solid #ddd;border-radius:6px;background:#fff;font:inherit}' +
      '.armastus-skel{border-radius:6px;background:linear-gradient(90deg,#f2f2f2 25%,#e9e9e9 37%,#f2f2f2 63%);background-size:400% 100%;animation:armastusShimmer 1.4s ease infinite}' +
      '.armastus-skel-img{width:100%;padding-bottom:var(--grid-img_ratio,130%);border-radius:6px}' +
      '.armastus-skel-line{height:12px;margin-top:10px;width:80%}' +
      '.armastus-skel-line.short{width:45%}' +
      '@keyframes armastusShimmer{0%{background-position:100% 50%}100%{background-position:0 50%}}' +
      '.armastus-infinite{text-align:center;padding:22px 0;width:100%;min-height:1px}' +
      '.armastus-spin{display:inline-block;width:26px;height:26px;border:2px solid rgba(0,0,0,.18);border-top-color:rgba(0,0,0,.6);border-radius:50%;animation:armastusSpin .7s linear infinite}' +
      '@keyframes armastusSpin{to{transform:rotate(360deg)}}' +
      '.armastus-msg{text-align:center;padding:30px 10px;opacity:.75;width:100%}' +
      '.armastus-camera{display:inline-flex;align-items:center;justify-content:center;background:transparent;border:0;cursor:pointer;padding:0 12px;color:inherit;margin-inline-start:4px}' +
      '.armastus-camera svg{width:20px;height:20px;display:block}' +
      '.armastus-visual-header{font-weight:600;margin:0 0 15px}' +
      '.armastus-toast{position:fixed;left:50%;bottom:24px;transform:translateX(-50%);max-width:90vw;background:#222;color:#fff;padding:12px 16px;border-radius:8px;font-size:14px;z-index:99999;box-shadow:0 4px 16px rgba(0,0,0,.25);display:flex;align-items:center;gap:12px}' +
      '.armastus-toast button{background:#fff;color:#222;border:0;border-radius:6px;padding:5px 10px;font:inherit;cursor:pointer}' +
      '[dir="rtl"] .armastus-toast{direction:rtl}' +
      // ---- Image-search "Search by photo" overlay ----
      'html.armastus-modal-open{overflow:hidden}' +
      '.armastus-imgmodal{position:fixed;inset:0;z-index:100000;display:none}' +
      '.armastus-imgmodal.is-open{display:block}' +
      '.armastus-imgmodal__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.5)}' +
      '.armastus-imgmodal__panel{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:640px;max-width:96vw;max-height:92vh;background:#fff;border-radius:12px;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 10px 40px rgba(0,0,0,.3)}' +
      '@media (max-width:600px){.armastus-imgmodal__panel{width:100vw;max-width:100vw;height:100%;max-height:100%;left:0;top:0;transform:none;border-radius:0}}' +
      '.armastus-imgmodal__head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:13px 16px;border-bottom:1px solid #eee;flex:0 0 auto}' +
      '.armastus-imgmodal__title{font-weight:600;font-size:15px}' +
      '.armastus-imgmodal__close{background:transparent;border:0;font-size:24px;line-height:1;cursor:pointer;padding:2px 8px;color:inherit}' +
      // Plain scrolling block (NOT a flex column): a flex column shrinks the photo
      // when the results grid renders below it, and the wrap''s overflow:hidden then
      // CROPS the image. Block layout + min-height:0 lets the body scroll while the
      // photo and controls keep their natural size.
      '.armastus-imgmodal__body{overflow-y:auto;-webkit-overflow-scrolling:touch;flex:1 1 auto;min-height:0;display:block;text-align:center}' +
      '.armastus-imgwrap{position:relative;display:inline-block;max-width:100%;margin:14px auto 0;line-height:0;background:#f3f3f3;border-radius:8px;overflow:hidden;touch-action:none}' +
      '.armastus-imgmodal__img{display:block;max-height:46vh;max-width:100%;width:auto;height:auto;-webkit-user-select:none;user-select:none;-webkit-user-drag:none;pointer-events:none}' +
      '.armastus-box{position:absolute;border:2px solid #fff;box-shadow:0 0 0 9999px rgba(0,0,0,.28);cursor:move;touch-action:none;box-sizing:border-box}' +
      '.armastus-handle{position:absolute;transform:translate(-50%,-50%);background:rgba(255,0,0,.82);border:2px solid #fff;border-radius:999px;box-shadow:0 1px 3px rgba(0,0,0,.4);touch-action:none;z-index:2}' +
      '.armastus-handle--corner{width:14px;height:14px}' +
      '.armastus-handle--edge-h{width:20px;height:9px}' +
      '.armastus-handle--edge-v{width:9px;height:20px}' +
      '.armastus-imgmodal__hint{text-align:center;font-size:12px;opacity:.6;margin:8px 14px 0}' +
      '.armastus-imgmodal__controls{display:flex;flex-wrap:wrap;gap:8px;justify-content:center;padding:12px 14px}' +
      '.armastus-imgmodal__controls button{cursor:pointer;font:inherit;font-size:14px;border-radius:8px;padding:9px 14px;line-height:1.1}' +
      // Short labels are desktop-hidden; shown only in the mobile one-row layout below.
      '.armastus-imgmodal__controls .al-short{display:none}' +
      // Mobile: ONE non-wrapping row of four equal-width compact buttons (short labels)
      // instead of wrapping to two rows under the photo. Still readable + tappable.
      '@media (max-width:600px){' +
        '.armastus-imgmodal__controls{flex-wrap:nowrap;gap:5px;padding:10px 8px}' +
        '.armastus-imgmodal__controls button{flex:1 1 0;min-width:0;font-size:11.5px;padding:8px 4px;border-radius:7px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
        '.armastus-imgmodal__controls .al-full{display:none}' +
        '.armastus-imgmodal__controls .al-short{display:inline}' +
      '}' +
      '.armastus-btn-ghost{background:#fff;border:1px solid #ddd;color:inherit}' +
      '.armastus-btn-primary{background:#FF0000;border:1px solid #FF0000;color:#fff;font-weight:600}' +
      '.armastus-imgmodal__status{text-align:center;padding:16px;opacity:.8;font-size:14px}' +
      '.armastus-imgmodal__matches{font-weight:600;font-size:14px;margin:6px 4px 12px;padding:0 14px;text-align:start}' +
      '.armastus-imgmodal__results{padding:0 14px 22px;overflow-x:hidden;text-align:start}' +
      '[dir="rtl"] .armastus-imgmodal{direction:rtl}';
    var style = document.createElement('style');
    style.id = 'armastus-search-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* -------------------------------------------------------------------------
   * Toast (for image-search errors)
   * ---------------------------------------------------------------------- */
  var toastTimer = null;
  function showToast(message, actionLabel, onAction) {
    var existing = document.querySelector('.armastus-toast');
    if (existing) existing.parentNode.removeChild(existing);
    if (toastTimer) clearTimeout(toastTimer);

    var toast = document.createElement('div');
    toast.className = 'armastus-toast';
    toast.setAttribute('role', 'status');
    if (RTL) toast.setAttribute('dir', 'rtl');
    var span = document.createElement('span');
    span.textContent = message;
    toast.appendChild(span);

    if (actionLabel && typeof onAction === 'function') {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = actionLabel;
      btn.addEventListener('click', function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
        onAction();
      });
      toast.appendChild(btn);
    }
    document.body.appendChild(toast);
    toastTimer = setTimeout(function () {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, actionLabel ? 9000 : 5000);
  }

  /* =========================================================================
   * MODULE 1 — Dropdown (predictive)
   * ===================================================================== */
  function findDrawerParts(input) {
    // Each search form lives inside a .search-drawer that also holds #serch-list
    // and #search-suggest__Wrapper. Scope by the closest drawer so multiple
    // headers/forms each drive their own list (native code targets the first;
    // we target the correct one per form).
    var drawer = input.closest ? input.closest('.search-drawer') : null;
    var scope = drawer || document;
    return {
      drawer: drawer,
      list: scope.querySelector('#serch-list'),
      idle: scope.querySelector('#search-suggest__Wrapper')
    };
  }

  function renderDropdown(parts, query, data) {
    var list = parts.list, idle = parts.idle;
    if (!list) return;
    var results = (data && data.results) || [];

    var html = '';
    if (results.length === 0) {
      html = '<p class="noResult tc armastus-msg">' + escapeHtml(STR.noResults()) + '</p>';
    } else {
      html += '<p class="reg-text ft2 mb10">' + escapeHtml(t('products', 'Products', 'المنتجات')) + '</p>';
      html += '<hr class="mt0 mb20" />';
      html += '<ul id="predictive-search-results-list" class="fl1 grid gspace rwcols-2 rwcols-md-3 rwcols-lg-4 predictive-search__results-list" style="--grsp:7.5px;--grspm:5px;">';
      for (var i = 0; i < results.length; i++) {
        html += '<li class="gitem">' + dropdownCardHTML(results[i]) + '</li>';
      }
      html += '</ul>';
      var seeAllHref = SEARCH_URL + '?q=' + encodeURIComponent(query);
      html += '<p class="tc"><a class="btn armastus-seeall" href="' + escapeHtml(seeAllHref) + '">' +
        escapeHtml(STR.seeAll()) + '</a></p>';
    }

    list.innerHTML = html;
    list.classList.remove('hide');
    if (idle) idle.classList.add('hide');
    bindCardClicks(list, function () { return query; });
  }

  function clearDropdown(parts) {
    if (parts.list) {
      parts.list.innerHTML = '';
      parts.list.classList.add('hide');
    }
    if (parts.idle) parts.idle.classList.remove('hide');
  }

  function attachDropdown(input) {
    var run = debounce(function () {
      var term = (input.value || '').trim();
      var parts = findDrawerParts(input);
      if (!parts.list) return;

      if (term.length < MIN_QUERY) {
        clearDropdown(parts);
        return;
      }
      setBusy(parts.list, true);
      apiSearch({
        q: term,
        lang: LANG,
        limit: DROPDOWN_LIMIT,
        page: 1,
        sort: 'relevance',
        source: SOURCE
      }).then(function (data) {
        // Guard against a stale response for an old keystroke
        if ((input.value || '').trim() !== term) { setBusy(parts.list, false); return; }
        setBusy(parts.list, false);
        renderDropdown(parts, term, data);
      }).catch(function () {
        // Graceful fallback: hide our list so the native <form> submit to /search wins.
        setBusy(parts.list, false);
        clearDropdown(parts);
      });
    }, 250);

    input.addEventListener('input', run);
    input.addEventListener('keyup', function (e) {
      // Enter just submits the native form; nothing to do.
      if (e.key === 'Enter') return;
      run();
    });
    input.addEventListener('focus', function () {
      if ((input.value || '').trim().length >= MIN_QUERY) run();
    });
  }

  // Neutralize native theme.js predictive handlers WITHOUT editing theme.js:
  // clone-replace each form.search input[name="q"] (cloneNode drops listeners),
  // then bind our own. The .searchIcn / .searchinline drawer-open handlers are
  // bound to OTHER elements (the icon / wrapper) so they survive untouched.
  function initDropdowns() {
    var forms = document.querySelectorAll('form.search');
    forms.forEach(function (form) {
      var input = form.querySelector('input[name="q"]');
      if (!input || input.__armastusBound) return;
      var fresh = input.cloneNode(true); // drops native focus/keyup/change listeners
      fresh.__armastusBound = true;
      input.parentNode.replaceChild(fresh, input);
      attachDropdown(fresh);
    });
  }

  /* =========================================================================
   * MODULE 2 — Results-page takeover
   * ===================================================================== */
  var resultsState = {
    mount: null,
    nativeGrid: null,
    query: '',
    page: 1,
    found: 0,
    totalPages: 1,
    sort: 'relevance',
    inStockOnly: false,
    loading: false,
    gridEl: null,
    sentinel: null,
    observer: null
  };

  function getNativeGrid() {
    // Prefer the explicit marker the theme edit (Edit C) puts on the native
    // results wrapper — deterministic, so we hide/reveal EXACTLY that block and
    // never miss it on this theme (its wrapper id is dynamic: id="{{ section.id }}").
    // The selector guesses remain only as a safety net if the marker was skipped.
    return document.querySelector('[data-armastus-native]') ||
           document.querySelector('#collection-products') ||
           document.querySelector('.grid-products') ||
           document.querySelector('collection-filters-form');
  }

  function hideNativeGrid() {
    var g = getNativeGrid();
    if (g) {
      g.setAttribute('data-armastus-hidden', 'true');
      g.style.display = 'none';
    }
    resultsState.nativeGrid = g;
  }

  function revealNativeGrid() {
    var g = resultsState.nativeGrid || getNativeGrid();
    if (g) {
      g.removeAttribute('data-armastus-hidden');
      g.style.display = '';
    }
    if (resultsState.mount) resultsState.mount.hidden = true;
  }

  // Skeleton cells as theme grid items (caller puts them inside the theme grid).
  function skeletonHTML(count) {
    var cells = '';
    for (var i = 0; i < count; i++) {
      cells +=
        '<div class="gitem fl" aria-hidden="true">' +
          '<div class="armastus-skel armastus-skel-img"></div>' +
          '<div class="armastus-skel armastus-skel-line"></div>' +
          '<div class="armastus-skel armastus-skel-line short"></div>' +
        '</div>';
    }
    return cells;
  }

  function buildResultsShell() {
    var mount = resultsState.mount;
    var html =
      // Width wrapper copied from the native #Collection so the grid gets the same
      // side gutter + max-width + centering (fixes the flush-left first column).
      '<div class="' + nativeContainerClasses() + '">' +
      '<div class="armastus-toolbar" role="group">' +
        '<label>' +
          '<span>' + escapeHtml(t('sortLabel', 'Sort', 'الترتيب')) + '</span>' +
          '<select data-armastus-sort aria-label="' + escapeHtml(t('sortLabel', 'Sort', 'الترتيب')) + '">' +
            '<option value="relevance">' + escapeHtml(STR.sortRelevance()) + '</option>' +
            '<option value="newest">' + escapeHtml(STR.sortNewest()) + '</option>' +
          '</select>' +
        '</label>' +
        '<label>' +
          '<input type="checkbox" data-armastus-instock> ' +
          '<span>' + escapeHtml(STR.inStockOnly()) + '</span>' +
        '</label>' +
        '<span class="armastus-count" data-armastus-count aria-live="polite"></span>' +
      '</div>' +
      // Theme grid container (classes copied from the live #collection-products) so
      // columns/spacing/padding match the native collection exactly.
      '<div class="' + nativeGridClasses() + '" data-armastus-grid></div>' +
      '<div class="armastus-infinite" data-armastus-sentinel hidden></div>' +
      '</div>';
    mount.innerHTML = html;
    resultsState.gridEl = mount.querySelector('[data-armastus-grid]');
    resultsState.sentinel = mount.querySelector('[data-armastus-sentinel]');

    var sortSel = mount.querySelector('[data-armastus-sort]');
    if (sortSel) {
      sortSel.value = resultsState.sort;
      sortSel.addEventListener('change', function () {
        resultsState.sort = sortSel.value === 'newest' ? 'newest' : 'relevance';
        loadResults(true);
      });
    }
    var inStock = mount.querySelector('[data-armastus-instock]');
    if (inStock) {
      inStock.checked = resultsState.inStockOnly;
      inStock.addEventListener('change', function () {
        resultsState.inStockOnly = !!inStock.checked;
        loadResults(true);
      });
    }
    bindCardClicks(mount, function () { return resultsState.query; });
    setupInfinite();
  }

  function updateCount() {
    var mount = resultsState.mount;
    if (!mount) return;
    var el = mount.querySelector('[data-armastus-count]');
    if (!el) return;
    var shown = resultsState.found >= MAX_PAGE ? (MAX_PAGE + '+') : String(resultsState.found);
    el.textContent = t('resultsCount', 'Results', 'النتائج') + ': ' + shown;
  }

  // Infinite scroll: observe a bottom sentinel; when it nears the viewport, load
  // the next page (stops at totalPages / the 200-hit cap).
  function setupInfinite() {
    if (!resultsState.sentinel || !('IntersectionObserver' in window)) return;
    if (resultsState.observer) resultsState.observer.disconnect();
    resultsState.observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting && !resultsState.loading) {
          var hasMore = resultsState.page < resultsState.totalPages && resultsState.page < MAX_PAGE;
          if (hasMore) { resultsState.page += 1; loadResults(false); }
        }
      }
    }, { rootMargin: '600px 0px' });
    resultsState.observer.observe(resultsState.sentinel);
  }

  function updateInfinite() {
    var s = resultsState.sentinel, obs = resultsState.observer;
    if (!s) return;
    var hasMore = resultsState.page < resultsState.totalPages && resultsState.page < MAX_PAGE;
    if (hasMore) {
      s.hidden = false;
      s.innerHTML = '<span class="armastus-spin" aria-hidden="true"></span>';
      // re-observe so a still-in-view sentinel chains to the next page after appending
      if (obs) { obs.unobserve(s); obs.observe(s); }
    } else {
      s.hidden = true;
      s.innerHTML = '';
      if (obs) obs.unobserve(s);
    }
  }

  function appendResults(results) {
    var grid = resultsState.gridEl;
    if (!grid) return;
    if (!results.length) return;
    var frag = '';
    for (var i = 0; i < results.length; i++) {
      // gridCardHTML returns a .gitem cell that drops straight into our CSS grid
      frag += gridCardHTML(results[i]);
    }
    grid.insertAdjacentHTML('beforeend', frag);
  }

  function loadResults(reset) {
    if (!resultsState.mount) return;
    resultsState.loading = true;
    setBusy(resultsState.mount, true);

    if (reset) {
      resultsState.page = 1;
      if (resultsState.gridEl) resultsState.gridEl.innerHTML = skeletonHTML(8);
    }

    apiSearch({
      q: resultsState.query,
      lang: LANG,
      page: resultsState.page,
      limit: RESULTS_LIMIT,
      sort: resultsState.sort,
      inStockOnly: resultsState.inStockOnly ? 'true' : 'false',
      source: SOURCE
    }).then(function (data) {
      resultsState.loading = false;
      setBusy(resultsState.mount, false);
      resultsState.found = (data && typeof data.found === 'number') ? data.found : 0;
      resultsState.totalPages = (data && typeof data.totalPages === 'number') ? data.totalPages : 1;
      var results = (data && data.results) || [];

      if (reset && resultsState.gridEl) {
        resultsState.gridEl.innerHTML = '';
        if (results.length === 0) {
          resultsState.gridEl.innerHTML = '<p class="armastus-msg">' + escapeHtml(STR.noResults()) + '</p>';
        }
      }
      appendResults(results);
      updateCount();
      updateInfinite();
    }).catch(function () {
      resultsState.loading = false;
      setBusy(resultsState.mount, false);
      // On any API failure, fall back to the native Liquid grid (SEO/no-JS path).
      revealNativeGrid();
    });
  }

  function initResultsPage() {
    var mount = document.getElementById('armastus-results');
    if (!mount) return;
    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    if (q == null || q === '') return; // no query -> leave native grid as-is

    resultsState.mount = mount;
    resultsState.query = q;
    // sort/inStock from URL if present (lets shared links preserve state)
    var urlSort = params.get('armastus_sort');
    if (urlSort === 'newest') resultsState.sort = 'newest';
    if (params.get('armastus_instock') === 'true') resultsState.inStockOnly = true;

    // Hide native grid IMMEDIATELY (avoid flash of differing Shopify results),
    // show our shell + skeleton, then fetch.
    hideNativeGrid();
    mount.hidden = false;
    buildResultsShell();
    loadResults(true);
  }

  /* =========================================================================
   * MODULE 3 — Image (visual) search — WMS-style "Search by photo" overlay
   *   Pick a photo -> the backend auto-detects & boxes the garment -> we auto-
   *   search that area. The customer can drag/resize the box and re-search, or
   *   search the whole photo. Self-contained overlay (works from any page);
   *   matched products render inside it as native-look cards.
   * ===================================================================== */

  // --- image decode + client-side crop/downscale (ported from the WMS client) ---
  // The backend only ever receives a pre-cropped, downscaled JPEG — never crop
  // coordinates — so no extra server contract is needed for "search by area".
  function getDrawable(file) {
    if (typeof createImageBitmap === 'function') {
      return createImageBitmap(file).then(function (bm) {
        return { src: bm, w: bm.width, h: bm.height, close: function () { if (bm && bm.close) bm.close(); } };
      }).catch(function () { return getDrawableViaImg(file); });
    }
    return getDrawableViaImg(file);
  }
  function getDrawableViaImg(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var im = new Image();
      im.onload = function () { resolve({ src: im, w: im.naturalWidth, h: im.naturalHeight, close: function () { URL.revokeObjectURL(url); } }); };
      im.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode')); };
      im.src = url;
    });
  }
  // Draw the box's source rect (or the whole image) to a canvas downscaled to
  // MAX_EDGE, returning a JPEG Blob. Falls back to the original file on any error.
  function cropAndDownscale(file, box) {
    return getDrawable(file).then(function (d) {
      try {
        var fullW = d.w, fullH = d.h;
        var hasCrop = !!box && box.w > 0 && box.h > 0 &&
          (box.x > 0 || box.y > 0 || box.w < 1 || box.h < 1);
        var sx = hasCrop ? Math.round(clamp(box.x, 0, 1) * fullW) : 0;
        var sy = hasCrop ? Math.round(clamp(box.y, 0, 1) * fullH) : 0;
        var sW = hasCrop ? Math.max(1, Math.round(clamp(box.w, 0, 1) * fullW)) : fullW;
        var sH = hasCrop ? Math.max(1, Math.round(clamp(box.h, 0, 1) * fullH)) : fullH;
        var scale = Math.min(1, MAX_EDGE / Math.max(sW, sH));
        if (!hasCrop && scale >= 1) { d.close(); return file; } // small whole image -> send as-is
        var w = Math.max(1, Math.round(sW * scale));
        var h = Math.max(1, Math.round(sH * scale));
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        var ctx = canvas.getContext('2d');
        if (!ctx) { d.close(); return file; }
        ctx.drawImage(d.src, sx, sy, sW, sH, 0, 0, w, h);
        d.close();
        return new Promise(function (resolve) {
          canvas.toBlob(function (blob) { resolve(blob || file); }, 'image/jpeg', JPEG_QUALITY);
        });
      } catch (e) {
        if (d && d.close) d.close();
        return file;
      }
    }).catch(function () { return file; });
  }
  // Ask the backend to box the garment in the WHOLE image. Best-effort -> [].
  function detectRegion(file) {
    return cropAndDownscale(file, null).then(function (blob) {
      return apiVisualDetect(blob);
    }).then(function (r) {
      return (r && r.detections) || [];
    }).catch(function () { return []; });
  }

  // --- overlay state + box helpers ---
  var imgState = {
    open: false, file: null, objectUrl: null,
    box: null, drag: null, reqId: 0, activeMode: 'whole',
    fileInput: null,
    overlay: null, imgEl: null, boxEl: null, wrapEl: null,
    statusEl: null, matchesEl: null, gridEl: null, resultsEl: null
  };
  function cloneBox(b) { return { x: b.x, y: b.y, w: b.w, h: b.h }; }
  function usefulBox(b) {
    if (!b) return false;
    var area = b.w * b.h;
    return b.w > 0 && b.h > 0 && area > 0.02 && area < 0.98;
  }
  function sanitizeBox(b) {
    var x = clamp(b.x, 0, 1 - MIN_BOX), y = clamp(b.y, 0, 1 - MIN_BOX);
    var w = clamp(b.w, MIN_BOX, 1 - x), h = clamp(b.h, MIN_BOX, 1 - y);
    return { x: x, y: y, w: w, h: h };
  }

  // --- box interaction (Pointer Events; mouse + touch share one path) ---
  function pct(n) { return (n * 100) + '%'; }
  function renderBox() {
    var b = imgState.box, boxEl = imgState.boxEl, ov = imgState.overlay;
    if (!b || !boxEl || !ov) return;
    boxEl.style.left = pct(b.x); boxEl.style.top = pct(b.y);
    boxEl.style.width = pct(b.w); boxEl.style.height = pct(b.h);
    var cx = b.x + b.w / 2, cy = b.y + b.h / 2;
    var posMap = {
      tl: [b.x, b.y], tr: [b.x + b.w, b.y], bl: [b.x, b.y + b.h], br: [b.x + b.w, b.y + b.h],
      t: [cx, b.y], b: [cx, b.y + b.h], l: [b.x, cy], r: [b.x + b.w, cy]
    };
    var handles = ov.querySelectorAll('[data-handle]');
    for (var i = 0; i < handles.length; i++) {
      var p = posMap[handles[i].getAttribute('data-handle')];
      if (p) { handles[i].style.left = pct(p[0]); handles[i].style.top = pct(p[1]); }
    }
  }
  function boxPointerDown(e, mode) {
    e.preventDefault(); e.stopPropagation();
    var img = imgState.imgEl; if (!img) return;
    var rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    imgState.drag = {
      mode: mode, startX: e.clientX, startY: e.clientY,
      startRect: cloneBox(imgState.box), imgW: rect.width, imgH: rect.height
    };
    document.addEventListener('pointermove', boxPointerMove);
    document.addEventListener('pointerup', boxPointerUp);
    document.addEventListener('pointercancel', boxPointerUp);
  }
  function boxPointerMove(e) {
    var d = imgState.drag; if (!d) return;
    var dx = (e.clientX - d.startX) / d.imgW;
    var dy = (e.clientY - d.startY) / d.imgH;
    var s = d.startRect, m = d.mode, box;
    if (m === 'move') {
      box = { x: clamp(s.x + dx, 0, 1 - s.w), y: clamp(s.y + dy, 0, 1 - s.h), w: s.w, h: s.h };
    } else {
      var left = s.x, top = s.y, right = s.x + s.w, bottom = s.y + s.h;
      if (m === 'tl' || m === 'bl' || m === 'l') left = clamp(s.x + dx, 0, right - MIN_BOX);
      if (m === 'tr' || m === 'br' || m === 'r') right = clamp(right + dx, left + MIN_BOX, 1);
      if (m === 'tl' || m === 'tr' || m === 't') top = clamp(s.y + dy, 0, bottom - MIN_BOX);
      if (m === 'bl' || m === 'br' || m === 'b') bottom = clamp(bottom + dy, top + MIN_BOX, 1);
      box = { x: left, y: top, w: right - left, h: bottom - top };
    }
    imgState.box = box;
    renderBox();
  }
  function boxPointerUp() {
    imgState.drag = null;
    document.removeEventListener('pointermove', boxPointerMove);
    document.removeEventListener('pointerup', boxPointerUp);
    document.removeEventListener('pointercancel', boxPointerUp);
  }

  // --- overlay build / open / close ---
  function ensureOverlay() {
    if (imgState.overlay) return imgState.overlay;
    var ov = document.createElement('div');
    ov.className = 'armastus-imgmodal';
    ov.setAttribute('role', 'dialog');
    ov.setAttribute('aria-modal', 'true');
    ov.setAttribute('aria-label', STR.searchByPhoto());
    if (RTL) ov.setAttribute('dir', 'rtl');
    var cornerCur = { tl: 'nwse-resize', tr: 'nesw-resize', bl: 'nesw-resize', br: 'nwse-resize' };
    function handle(mode, cls, cur) {
      return '<span class="armastus-handle ' + cls + '" data-handle="' + mode + '" style="cursor:' + cur + '"></span>';
    }
    ov.innerHTML =
      '<div class="armastus-imgmodal__backdrop" data-close></div>' +
      '<div class="armastus-imgmodal__panel">' +
        '<div class="armastus-imgmodal__head">' +
          '<span class="armastus-imgmodal__title">' + escapeHtml(STR.searchByPhoto()) + '</span>' +
          '<button type="button" class="armastus-imgmodal__close" data-close aria-label="' + escapeHtml(STR.close()) + '">&times;</button>' +
        '</div>' +
        '<div class="armastus-imgmodal__body">' +
          '<div class="armastus-imgwrap" data-imgwrap>' +
            '<img class="armastus-imgmodal__img" data-img alt="">' +
            '<div class="armastus-box" data-box></div>' +
            handle('tl', 'armastus-handle--corner', cornerCur.tl) +
            handle('tr', 'armastus-handle--corner', cornerCur.tr) +
            handle('bl', 'armastus-handle--corner', cornerCur.bl) +
            handle('br', 'armastus-handle--corner', cornerCur.br) +
            handle('t', 'armastus-handle--edge-h', 'ns-resize') +
            handle('b', 'armastus-handle--edge-h', 'ns-resize') +
            handle('l', 'armastus-handle--edge-v', 'ew-resize') +
            handle('r', 'armastus-handle--edge-v', 'ew-resize') +
          '</div>' +
          '<p class="armastus-imgmodal__hint">' + escapeHtml(STR.dragHint()) + '</p>' +
          '<div class="armastus-imgmodal__controls">' +
            '<button type="button" class="armastus-btn-primary" data-area><span class="al-full">' + escapeHtml(STR.searchThisArea()) + '</span><span class="al-short">' + escapeHtml(STR.searchThisAreaShort()) + '</span></button>' +
            '<button type="button" class="armastus-btn-ghost" data-whole><span class="al-full">' + escapeHtml(STR.searchWholePhoto()) + '</span><span class="al-short">' + escapeHtml(STR.searchWholePhotoShort()) + '</span></button>' +
            '<button type="button" class="armastus-btn-ghost" data-reset><span class="al-full">&#8634; ' + escapeHtml(STR.resetArea()) + '</span><span class="al-short">' + escapeHtml(STR.resetAreaShort()) + '</span></button>' +
            '<button type="button" class="armastus-btn-ghost" data-choose><span class="al-full">' + escapeHtml(STR.choosePhoto()) + '</span><span class="al-short">' + escapeHtml(STR.choosePhotoShort()) + '</span></button>' +
          '</div>' +
          '<div class="armastus-imgmodal__results" data-results>' +
            '<div class="armastus-imgmodal__status" data-status style="display:none"></div>' +
            '<div class="armastus-imgmodal__matches" data-matches></div>' +
            '<div data-grid></div>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(ov);

    imgState.overlay = ov;
    imgState.imgEl = ov.querySelector('[data-img]');
    imgState.boxEl = ov.querySelector('[data-box]');
    imgState.wrapEl = ov.querySelector('[data-imgwrap]');
    imgState.statusEl = ov.querySelector('[data-status]');
    imgState.matchesEl = ov.querySelector('[data-matches]');
    imgState.gridEl = ov.querySelector('[data-grid]');
    imgState.resultsEl = ov.querySelector('[data-results]');

    imgState.boxEl.addEventListener('pointerdown', function (e) { boxPointerDown(e, 'move'); });
    ov.querySelectorAll('[data-handle]').forEach(function (hEl) {
      hEl.addEventListener('pointerdown', function (e) { boxPointerDown(e, hEl.getAttribute('data-handle')); });
    });
    // re-place the box once the image has laid out at its rendered size
    imgState.imgEl.addEventListener('load', renderBox);

    ov.querySelectorAll('[data-close]').forEach(function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); closeOverlay(); });
    });
    ov.querySelector('[data-area]').addEventListener('click', function (e) { e.preventDefault(); runSearch(true); });
    ov.querySelector('[data-whole]').addEventListener('click', function (e) { e.preventDefault(); runSearch(false); });
    ov.querySelector('[data-reset]').addEventListener('click', function (e) { e.preventDefault(); imgState.box = cloneBox(DEFAULT_BOX); renderBox(); });
    ov.querySelector('[data-choose]').addEventListener('click', function (e) {
      e.preventDefault();
      if (imgState.fileInput) imgState.fileInput.click();
    });
    bindCardClicks(imgState.resultsEl, function () { return ''; });

    document.addEventListener('keydown', function (e) {
      if (imgState.open && (e.key === 'Escape' || e.key === 'Esc')) closeOverlay();
    });
    return ov;
  }

  function setOverlayPhoto(file) {
    if (imgState.objectUrl) { try { URL.revokeObjectURL(imgState.objectUrl); } catch (e) {} }
    imgState.file = file;
    imgState.objectUrl = URL.createObjectURL(file);
    imgState.imgEl.src = imgState.objectUrl;
    imgState.box = cloneBox(DEFAULT_BOX);
    renderBox();
    if (imgState.matchesEl) imgState.matchesEl.textContent = '';
    if (imgState.gridEl) imgState.gridEl.innerHTML = '';
  }

  function openImageOverlay(file) {
    if (!/^image\//.test(file.type || '')) { showToast(STR.imageWrongType()); return; }
    if (file.size > MAX_IMAGE_BYTES) { showToast(STR.imageTooLarge()); return; }
    ensureOverlay();
    setOverlayPhoto(file);
    imgState.open = true;
    imgState.overlay.classList.add('is-open');
    document.documentElement.classList.add('armastus-modal-open');
    var closeBtn = imgState.overlay.querySelector('.armastus-imgmodal__close');
    if (closeBtn && closeBtn.focus) { try { closeBtn.focus(); } catch (e) {} }
    autoDetectAndSearch(file);
  }

  function closeOverlay() {
    imgState.open = false;
    imgState.reqId++; // cancel any in-flight detect/search render
    if (imgState.overlay) imgState.overlay.classList.remove('is-open');
    document.documentElement.classList.remove('armastus-modal-open');
    if (imgState.objectUrl) { try { URL.revokeObjectURL(imgState.objectUrl); } catch (e) {} imgState.objectUrl = null; }
  }

  function setOverlayStatus(msg, busy) {
    var s = imgState.statusEl; if (!s) return;
    if (!msg) { s.innerHTML = ''; s.style.display = 'none'; return; }
    s.style.display = '';
    s.innerHTML = (busy ? '<span class="armastus-spin" aria-hidden="true"></span> ' : '') + escapeHtml(msg);
    if (busy) {
      if (imgState.matchesEl) imgState.matchesEl.textContent = '';
      if (imgState.gridEl) imgState.gridEl.innerHTML = '';
    }
  }

  // Pick photo -> "Finding the item…" (detect) -> if a confident, useful box comes
  // back, snap to it and search that area; otherwise search the whole photo.
  function autoDetectAndSearch(file) {
    var rid = ++imgState.reqId;
    setOverlayStatus(STR.findingItem(), true);
    imgState.box = cloneBox(DEFAULT_BOX);
    renderBox();
    detectRegion(file).then(function (dets) {
      if (rid !== imgState.reqId || !imgState.open) return;
      var top = dets && dets[0];
      if (top && typeof top.score === 'number' && top.score >= AUTO_DETECT_MIN_SCORE && usefulBox(top.box)) {
        imgState.box = sanitizeBox(top.box);
        renderBox();
        runSearch(true);
      } else {
        runSearch(false);
      }
    }).catch(function () {
      if (rid !== imgState.reqId || !imgState.open) return;
      runSearch(false);
    });
  }

  function runSearch(useArea) {
    var file = imgState.file; if (!file) return;
    var rid = ++imgState.reqId;
    imgState.activeMode = useArea ? 'area' : 'whole';
    setOverlayStatus(STR.searchingCatalog(), true);
    cropAndDownscale(file, useArea ? imgState.box : null).then(function (blob) {
      if (rid !== imgState.reqId || !imgState.open) return null;
      return apiVisualSearch(blob);
    }).then(function (data) {
      if (data === null || rid !== imgState.reqId || !imgState.open) return;
      renderOverlayResults((data && data.results) || []);
    }).catch(function (err) {
      if (rid !== imgState.reqId || !imgState.open) return;
      handleVisualError(err);
    });
  }

  function handleVisualError(err) {
    var status = err && err.status, msg;
    if (status === 503) msg = STR.imageWarming();
    else if (status === 429) msg = STR.imageBusy();
    else if (status === 422) msg = STR.imageUnreadable();
    else if (status === 400) msg = STR.imageWrongType();
    else msg = STR.imageError();
    setOverlayStatus(msg, false);
    if (status === 503) {
      showToast(msg, STR.retry(), function () { runSearch(imgState.activeMode === 'area'); });
    }
  }

  function renderOverlayResults(results) {
    setOverlayStatus('', false);
    var grid = imgState.gridEl, matches = imgState.matchesEl;
    if (!grid) return;
    if (!results.length) {
      if (matches) matches.textContent = '';
      grid.className = '';
      grid.innerHTML = '<p class="armastus-msg">' + escapeHtml(STR.noResults()) + '</p>';
      return;
    }
    var n = Math.min(results.length, VISUAL_RESULTS_LIMIT);
    if (matches) {
      var shown = results.length > VISUAL_RESULTS_LIMIT ? (VISUAL_RESULTS_LIMIT + '+') : String(results.length);
      matches.textContent = STR.matches() + ' (' + shown + ')';
    }
    var html = '';
    for (var i = 0; i < n; i++) html += gridCardHTML(results[i]);
    grid.className = OVERLAY_GRID_CLS;
    grid.innerHTML = html;
  }

  function initImageSearch() {
    var cameras = document.querySelectorAll('[data-armastus-camera]');
    if (!cameras.length) return;
    // Open a normal upload picker (gallery/files) instead of jumping straight to the
    // camera: the theme markup sets capture="environment" on the input, which forces
    // the camera on mobile. Strip it so shoppers can upload an existing photo (they
    // can still choose "Take Photo" from the picker). No theme edit needed.
    var fileInputs = document.querySelectorAll('[data-armastus-file]');
    for (var fi = 0; fi < fileInputs.length; fi++) fileInputs[fi].removeAttribute('capture');
    cameras.forEach(function (cam) {
      if (cam.__armastusBound) return;
      cam.__armastusBound = true;

      // Locate the file input: prefer one inside the same form, else nearest.
      var form = cam.closest ? cam.closest('form') : null;
      var fileInput = (form && form.querySelector('[data-armastus-file]')) ||
        document.querySelector('[data-armastus-file]');

      cam.addEventListener('click', function (e) {
        e.preventDefault();
        if (fileInput) fileInput.click();
      });

      if (fileInput && !fileInput.__armastusBound) {
        fileInput.__armastusBound = true;
        imgState.fileInput = fileInput; // let the overlay's "Choose photo" re-open it
        fileInput.addEventListener('change', function () {
          var file = fileInput.files && fileInput.files[0];
          if (file) openImageOverlay(file);
          // reset so selecting the same file again re-triggers change
          fileInput.value = '';
        });
      }
    });
    // Fallback so "Choose photo" always has an input to click, even if bound late.
    if (!imgState.fileInput) imgState.fileInput = document.querySelector('[data-armastus-file]');
  }

  /* =========================================================================
   * Boot
   * ===================================================================== */
  ready(function () {
    try { injectStylesOnce(); } catch (e) {}
    try { initSwatchSwap(); } catch (e) {}
    try { initDropdowns(); } catch (e) {}
    try { initResultsPage(); } catch (e) {}
    try { initImageSearch(); } catch (e) {}
  });
})();
